import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Backdrop from "@mui/material/Backdrop";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import { useMergedListItems, useMyGames } from "@seabrookstudios/pitch2table";
import { useHotkeys } from "react-hotkeys-hook";
import List from "@mui/material/List";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { CoverSquare } from "./games/CoverImages";
import { useNavigate } from "react-router-dom";
import { Path } from "./Path";
import { useMergedPublishers } from "./publishers/use-merged-publishers";
import { PublisherLogoSquare2 } from "./publisher/PublisherLogo";
import { Features } from "./games/features";
import { ConventionLogoSquare } from "./events/ConventionLogo";

/**
 * @typedef {"game"|"publisher"|"event"} QuickSearchItemType
 */
/**
 * @typedef {object} QuickSearchItem
 * @property {string} id
 * @property {QuickSearchItemType} type
 * @property {string} label
 * @property {string} [subtitle]
 * @property {() => void} onSelect
 * @property {React.ReactNode} avatar
 */

export const QuickSearchContext = createContext({ isShowing: false, show: () => {}, hide: () => {} });

/**
 * Build quick search items for games only.
 *
 * @param {import("@seabrookstudios/pitch2table-core").GameSummary[]} games
 * @param {import("react-router-dom").NavigateFunction} navigate
 * @param {() => void} hide
 * @returns {QuickSearchItem[]}
 */
const useQuickSearchGames = (games = [], navigate, hide) => {
  return useMemo(() => {
    return games.map(
      (game) =>
        /** @type {QuickSearchItem} */ ({
          id: game.id,
          type: "game",
          label: game.name ?? "",
          subtitle: game.tag ?? "",
          onSelect: () => {
            navigate(Path.game(game.id).link());
            hide();
          },
          avatar: (
            <ListItemAvatar>
              <CoverSquare game={game} width={48} height={48} />
            </ListItemAvatar>
          ),
        })
    );
  }, [games, navigate, hide]);
};

/**
 * Build quick search items for publishers only (when feature enabled).
 *
 * @param {import("@seabrookstudios/pitch2table-core").PublisherWithContactBookPublisher[]} mergedPublishers
 * @param {import("react-router-dom").NavigateFunction} navigate
 * @param {() => void} hide
 * @returns {QuickSearchItem[]}
 */
const useQuickSearchPublishers = (mergedPublishers = [], navigate, hide) => {
  return useMemo(() => {
    if (!Features.QuickSearchPublishers || mergedPublishers.length === 0) {
      return [];
    }

    /** @type {QuickSearchItem[]} */
    const publisherItems = mergedPublishers.map((mp) => {
      const [publisher, contactBookPublisher] = mp;
      const subtitle =
        contactBookPublisher && Array.isArray(contactBookPublisher.games)
          ? `${contactBookPublisher.games.length} submissions`
          : "";

      return {
        id: publisher.id.toString(),
        type: "publisher",
        label: publisher.name ?? "",
        subtitle,
        onSelect: () => {
          navigate(Path.publisher(publisher.id).link());
          hide();
        },
        avatar: (
          <ListItemAvatar>
            <PublisherLogoSquare2 publisher={/** @type {*} */ (publisher)} width={48} height={48} />
          </ListItemAvatar>
        ),
      };
    });

    return publisherItems;
  }, [mergedPublishers, navigate, hide]);
};

/**
 * Build quick search items for events only.
 *
 * @param {import("@seabrookstudios/pitch2table-core").ListItem[]} events
 * @param {import("react-router-dom").NavigateFunction} navigate
 * @param {() => void} hide
 * @returns {QuickSearchItem[]}
 */
const useQuickSearchEvents = (events = [], navigate, hide) => {
  return useMemo(
    () =>
      events.flatMap((item) => {
        if (!item) {
          return [];
        }

        const eventItem = /** @type {import("@seabrookstudios/pitch2table-core").ListItem} */ (item);

        if (eventItem.type !== "CalendarEvent") {
          // Ignore non-calendar-event items for now (e.g. sessions).
          return [];
        }

        const event = /** @type {import("@seabrookstudios/pitch2table-core").CalendarEvent} */ (eventItem.data);

        return [
          /** @type {QuickSearchItem} */ ({
            id: event.id.toString(),
            type: "event",
            label: event.name ?? "",
            subtitle: "",
            onSelect: () => {
              navigate(Path.events().event(event.id));
              hide();
            },
            avatar: (
              <ListItemAvatar>
                <ConventionLogoSquare convention={event} width={48} height={48} />
              </ListItemAvatar>
            ),
          }),
        ];
      }),
    [events, navigate, hide]
  );
};

/**
 * Build unified quick search items from games, publishers, and events.
 *
 * @param {import("@seabrookstudios/pitch2table-core").GameSummary[]} games
 * @param {import("@seabrookstudios/pitch2table-core").PublisherWithContactBookPublisher[]} mergedPublishers
 * @param {import("@seabrookstudios/pitch2table-core").ListItem[]} events
 * @param {import("react-router-dom").NavigateFunction} navigate
 * @param {() => void} hide
 * @returns {QuickSearchItem[]}
 */
const useQuickSearchItems = (games = [], mergedPublishers = [], events = [], navigate, hide) => {
  const gameItems = useQuickSearchGames(games, navigate, hide);
  const publisherItems = useQuickSearchPublishers(mergedPublishers, navigate, hide);
  const eventItems = useQuickSearchEvents(events, navigate, hide);

  return useMemo(() => [...gameItems, ...publisherItems, ...eventItems], [gameItems, publisherItems, eventItems]);
};

/**
 * @param {{ item: QuickSearchItem }} props
 */
const QuickSearchResultItemGame = ({ item }) => {
  return (
    <ListItemButton key={`game-${item.id}`} onClick={item.onSelect}>
      {item.avatar}
      <ListItemText primary={item.label} secondary={item.subtitle} />
      <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
        <Chip size="small" label="Game" color="primary" />
      </Stack>
    </ListItemButton>
  );
};

/**
 * @param {{ item: QuickSearchItem }} props
 */
const QuickSearchResultItemPublisher = ({ item }) => {
  return (
    <ListItemButton key={`publisher-${item.id}`} onClick={item.onSelect}>
      {item.avatar}
      <ListItemText primary={item.label} secondary={item.subtitle} />
      <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
        <Chip size="small" label="Publisher" color="secondary" />
      </Stack>
    </ListItemButton>
  );
};

/**
 * @param {{ item: QuickSearchItem }} props
 */
const QuickSearchResultItem = ({ item }) => {
  if (item.type === "game") {
    return <QuickSearchResultItemGame item={item} />;
  }

  if (item.type === "publisher") {
    return <QuickSearchResultItemPublisher item={item} />;
  }

  return (
    <ListItemButton key={`event-${item.id}`} onClick={item.onSelect}>
      {item.avatar}
      <ListItemText primary={item.label} secondary={item.subtitle} />
      <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
        <Chip size="small" label="Event" color="default" />
      </Stack>
    </ListItemButton>
  );
};

/**
 * @param {object} props
 * @param {import("react").ReactNode} [props.children]
 */
export const QuickSearchDisplay = ({ children }) => {
  const { data: games = [] } = useMyGames();
  const { data: mergedPublishers = [] } = useMergedPublishers();
  const { data: events = [] } = useMergedListItems(true);
  const { isShowing, show, hide } = useContext(QuickSearchContext);
  const [filter, setFilter] = useState("");
  const navigate = useNavigate();

  const allItems = useQuickSearchItems(games, mergedPublishers, events, navigate, hide);

  const filteredItems = useMemo(() => {
    const term = filter.trim().toLocaleLowerCase();
    if (!term) return [];

    const scored = allItems
      .map((item) => {
        const label = item.label.toLocaleLowerCase();
        const subtitle = (item.subtitle || "").toLocaleLowerCase();

        let score = 0;
        if (label === term) score = 3;
        else if (label.startsWith(term)) score = 2;
        else if (label.includes(term)) score = 1;
        else if (subtitle.includes(term)) score = 0.5;

        return { item, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score);

    return scored.map((x) => x.item);
  }, [allItems, filter]);

  const acceptedTop = () => {
    if (filteredItems.length === 0) {
      return;
    }

    filteredItems[0].onSelect();
  };

  // Use cmd+k on macOS and ctrl+f on Windows/Linux to avoid conflicting
  // with the browser's built-in ctrl+k behaviour (focuses the address bar).
  useHotkeys(["meta+k", "ctrl+f"], () => show(), { preventDefault: true });
  useHotkeys("escape", () => hide());

  if (!isShowing) {
    return children;
  }

  const updateFilter = (e) => {
    setFilter(e.target.value);
  };

  const checkForEscape = (e) => {
    if (e.key === "Escape") {
      hide();
    }
    if (e.key === "Enter") {
      acceptedTop();
    }
  };

  return (
    <>
      {children}
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={isShowing}
        onClick={() => hide()}
      >
        <Box width="66%" style={{ position: "absolute", top: "10%" }}>
          <Card>
            <Box p={2}>
              <TextField
                fullWidth
                label="Search"
                id="search"
                value={filter}
                autoFocus
                autoComplete="off"
                autoCorrect="off"
                onChange={updateFilter}
                onKeyDown={checkForEscape}
              />
              {filter.length > 0 && (
                <List>
                  {filteredItems.slice(0, 5).map((item) => (
                    <QuickSearchResultItem key={`${item.type}-${item.id}`} item={item} />
                  ))}
                </List>
              )}
            </Box>
          </Card>
        </Box>
      </Backdrop>
    </>
  );
};

/**
 * @param {object} props
 * @param {import('react').ReactNode} [props.children]
 */
export const QuickSearch = ({ children }) => {
  const [isShowing, setShowModal] = useState(false);

  const show = useCallback(() => {
    setShowModal(true);
  }, [setShowModal]);

  const hide = useCallback(() => {
    setShowModal(false);
  }, [setShowModal]);

  const value = useMemo(() => ({ show, hide, isShowing }), [show, hide, isShowing]);

  return (
    <QuickSearchContext.Provider value={value}>
      <QuickSearchDisplay>{children}</QuickSearchDisplay>
    </QuickSearchContext.Provider>
  );
};
